// polyfills
import '../../Opten.HypothekarbankLenzburg.Core.Static/utilities/polyfills';

import { linearTween } from '@opten/animation/linear-tween';
import { factory } from '@opten/compiler/factory';
import { Component } from '@opten/compiler/metadata/component';
import { getSelectorElementFromElement } from '@opten/dom/get-selector-element-from-element';
import { scrollToOffset } from '@opten/scroll/scroll-to-offset';

import { CollapseComponent } from '@opten/collapse';
import { MenuTriggerComponent } from '@opten/menu';
import { tooltip } from '@opten/tooltip';
import { breadcrumb } from './components/breadcrumb';

import { isPad, MOBILE_WIDTH } from './utilities/detection';
import { getStickyOffset } from './utilities/scroll-helper';

import cookieConsent from '../../Opten.HypothekarbankLenzburg.Core.Static/components/cookie-consent';
import filterpage from '../../Opten.HypothekarbankLenzburg.Core.Static/components/filterpage';
import filtermixitup from './components/filtermixitup';
import { FilterPageBlog } from './components/filterpage-blog';

import { Anchor } from '../../Opten.HypothekarbankLenzburg.Core.Static/components/anchor';
import Datepicker from '../../Opten.HypothekarbankLenzburg.Core.Static/components/datepicker';
import Dialog from '../../Opten.HypothekarbankLenzburg.Core.Static/components/dialog/dialog';
import { GtmTrackerComponent } from '../../Opten.HypothekarbankLenzburg.Core.Static/components/gtm-tracker';
import InputFieldsFilled from '../../Opten.HypothekarbankLenzburg.Core.Static/components/input-fields-filled';
import Photoswipe from '../../Opten.HypothekarbankLenzburg.Core.Static/components/photoswipe/photoswipe';
import { Player } from '../../Opten.HypothekarbankLenzburg.Core.Static/components/player/player';
import { PrintPageComponent } from '../../Opten.HypothekarbankLenzburg.Core.Static/components/print-page';
import recaptchaValidator from '../../Opten.HypothekarbankLenzburg.Core.Static/components/recaptcha';
import recaptcha3Validator from '../../Opten.HypothekarbankLenzburg.Core.Static/components/recaptcha-3';
import { SelectComponent } from '../../Opten.HypothekarbankLenzburg.Core.Static/components/select/select';
import InvestmentSavingsCalculator from './components/calculators/investment-savings-calculator';
import RetirementCalculator from './components/calculators/retirement-calculator';
import courseForm from './components/course-form';
import CrossSelling from './components/cross-selling';
import CustomerPagesFullPreview from './components/customer-pages-full-preview';
import formExtended from './components/form-extended';
import Header from './components/header';
import { MagicScrollComponent } from './components/magic-scroll';
import { MapExtendedComponent } from './components/map-extended';
import MortgageCalculator, {
	MortgageCalculatorCookieDelete,
	MortgageCalculatorCookieSave
} from './components/mortgage-calculator';
import MortgageFundingProposalForm from './components/mortgage-funding-proposal-form';
import { Navbar, NavbarMegaDropdown, NavbarToggler } from './components/navbar';
import { NotificationComponent } from './components/notification';
import { ParallaxSliderComponent } from './components/parallax-slider/parallax-slider';
import { Search, SearchToggler } from './components/search';
import { SearchAutocompleteComponent } from './components/search/search-autocomplete';
import Sidebar from './components/sidebar';
import Tangram from './components/tangram';
import { TextMaskCurrency } from './components/text-mask';
import { TileSliderComponent } from './components/tile-slider';

@Component({ className: 'js-recaptcha', observe: true })
class RecaptchaValidator {
	constructor(private element: HTMLElement) {
		recaptchaValidator(element);
	}
}

@Component({ className: 'js-recaptcha-3', observe: true })
class Recaptcha3Validator {
	constructor(private element: HTMLInputElement) {
		recaptcha3Validator(element);
	}
}

@Component({ className: 'js-course-form' })
class CourseForm {
	constructor(private element: HTMLElement) {
		courseForm(element);
	}
}

@Component({ className: 'js-form__field-accept', observe: true })
class FormFieldAccept {
	constructor(private element: HTMLElement) {
		if (element.hasAttribute('data-id')) {
			element.addEventListener('click', () => {
				const id = element.getAttribute('data-id');
				const checkbox = document.querySelector<HTMLInputElement>(
					'[id="' + id + '"]'
				);

				if (checkbox) {
					checkbox.checked = true;
				}
			});
		}
	}
}

@Component({ className: 'js-form-extended' })
class FormExtended {
	constructor(private element: HTMLElement) {
		formExtended(element);
	}
}

@Component({ className: 'js-filter__page' })
class FilterPage {
	constructor(private element: HTMLElement) {
		filterpage(element);
	}
}

@Component({ className: 'js-filter__mixitup' })
class FilterMixitup {
	constructor(private element: HTMLElement) {
		filtermixitup(element);
	}
}

@Component({ className: 'js-cookie-consent' })
class CookieConsent {
	constructor(private element: HTMLElement) {
		/*const header = document.getElementsByClassName(
            'js-header'
        )[0] as HTMLElement;*/
		const connectionIsSecure = element.hasAttribute('data-cookie-secure')
			? element.getAttribute('data-cookie-secure').toLowerCase() ===
			  'true'
			: true;
		cookieConsent([], {
			showAfter: 600,
			cookie: {
				expirationInDays: 365,
				secure: connectionIsSecure
			}
		});
	}
}

@Component({ className: 'js-breadcrumb' })
class Breadcrumb {
	constructor(private element: HTMLElement) {
		breadcrumb(element, {
			scrollToLabel: true,
			shrinkMode: 'Width',
			shrinkCap: 5,
			breakpoints: {
				maxWidth: MOBILE_WIDTH,
				shrinkMode: 'Width'
			}
		});
	}
}

@Component({ className: 'js-tooltip', observe: true })
class Tooltip {
	constructor(private element: HTMLElement) {
		tooltip(element);
	}
}

@Component({ className: 'js-accordion-header' })
class AccordionHeader {
	constructor(private element: HTMLElement) {
		const collapse = getSelectorElementFromElement(
			element,
			'data-target',
			true
		);

		if (collapse) {
			collapse.addEventListener('shown.opten.collapse', () => {
				if (isPad()) {
					const rect = element.getBoundingClientRect();

					scrollToOffset(
						rect.top,
						linearTween,
						150,
						getStickyOffset() * -1
					);
				}
			});
		}
	}
}

factory([
	Tangram,
	CrossSelling,
	CollapseComponent,
	SelectComponent,
	Photoswipe,
	Player,
	Header,
	Search,
	Navbar,
	NavbarMegaDropdown,
	NavbarToggler,
	SearchToggler,
	SearchAutocompleteComponent,
	Breadcrumb,
	MenuTriggerComponent,
	CookieConsent,
	MapExtendedComponent,
	Sidebar,
	CustomerPagesFullPreview,
	InputFieldsFilled,
	CourseForm,
	Tooltip,
	Dialog,
	FormFieldAccept,
	FormExtended,
	AccordionHeader,
	MortgageCalculator,
	MortgageCalculatorCookieDelete,
	MortgageCalculatorCookieSave,
	MortgageFundingProposalForm,
	InvestmentSavingsCalculator,
	RetirementCalculator,
	Datepicker,
	TextMaskCurrency,
	Anchor,
	FilterPage,
	FilterPageBlog,
	FilterMixitup,
	RecaptchaValidator,
	Recaptcha3Validator,
	TileSliderComponent,
	MagicScrollComponent,
	ParallaxSliderComponent,
	GtmTrackerComponent,
	PrintPageComponent,
	NotificationComponent
])(document.body);
